// ============================================
// Lens Distortion Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { LENS_DISTORTION_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Lens Distortion post-processing effect
 * Creates barrel or pincushion distortion
 */
export class LensDistortionEffect extends PostProcessEffect {
  readonly type = 'lensDistortion';
  readonly displayName = 'Lens Distortion';
  
  // Parameters
  distortion = 0.0;
  cubicDistortion = 0.0;
  scale = 1.0;
  centerX = 0.5;
  centerY = 0.5;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = LENS_DISTORTION_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'distortion', type: 'float', min: -1, max: 1, default: 0.0, description: 'Barrel/pincushion distortion' },
      { name: 'cubicDistortion', type: 'float', min: -1, max: 1, default: 0.0, description: 'Cubic distortion term' },
      { name: 'scale', type: 'float', min: 0.5, max: 2, default: 1.0, description: 'Scale factor' },
      { name: 'centerX', type: 'float', min: 0, max: 1, default: 0.5, description: 'Distortion center X' },
      { name: 'centerY', type: 'float', min: 0, max: 1, default: 0.5, description: 'Distortion center Y' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'distortion': return this.distortion;
      case 'cubicDistortion': return this.cubicDistortion;
      case 'scale': return this.scale;
      case 'centerX': return this.centerX;
      case 'centerY': return this.centerY;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'distortion': this.distortion = value; break;
      case 'cubicDistortion': this.cubicDistortion = value; break;
      case 'scale': this.scale = value; break;
      case 'centerX': this.centerX = value; break;
      case 'centerY': this.centerY = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const lensDistortionWgsl = getCompiledShader('postprocess/lens-distortion');
    this.shader = ctx.createShader(lensDistortionWgsl, lensDistortionWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lens-distortion-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lens-distortion-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      intensity: this.distortion * intensity,
      scale: 1.0 + (this.scale - 1.0) * intensity,
      center: [this.centerX, this.centerY],
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
